import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import './index.scss';
import ConfirmOrderModal from './ConfirmOrderModal';
import DeleteConfirmationModal from '../../admin/addUser/ShopDeleteModal';
import { useDispatch, useSelector } from 'react-redux';
import {
	addOrderByCustomer,
	checkStock,
	clearBasketCustomer,
	deleteBasket,
	getBasket,
	getAllContenairesList,
	handleChangeQuantity,
	updatedbasketTotalQty,
	udpateStockProduitBasket,
} from '../../../api/redux/slices/Customer';
import { DataErrorModal, showToast } from '../../../components/modal';
import { roundToThreeDecimals, typePrice } from '../../../helpers/modules';
import { calculateTotalTva } from '../../../utils/groupTvaValues';
import { ButtonComponent } from '../../../components/buttons';

const ProductCartClient = () => {
	const [confirmModal, setConfirmModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [indexDeleteCart, setIndexDeleteCart] = useState(null);

	const token = localStorage.getItem('accesstoken');
	const { basket, basketTotalQty, allContenaires } = useSelector((state) => state.customer);
	const [oldBaksetTotalQty, setOldBasketTotalQty] = useState(basketTotalQty);
	const { user } = useSelector((state) => state.auth);
	const [selectedBoutiqueId, setSelectedBoutiqueId] = useState('');
	const [outOfStock, setOutOfStock] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [errorData, setErrorData] = useState([]);
	const [isOpen, setIsOpen] = useState(false);

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const BoutiquesData = useMemo(
		() =>
			user.data.shop.map((el) => ({
				name: el.name,
				ville: el.ville,
				id: el._id,
				country: el.country,
				region: el.region,
			})),
		[user.data.shop],
	);

	const selectedBoutique = useMemo(
		() => user.data.shop.find((el) => el._id === selectedBoutiqueId),
		[user.data.shop, selectedBoutiqueId],
	);

	// ✅ Check if selected boutique is IN Tunisia
	const isTunisia = !selectedBoutique?.country || selectedBoutique?.country === 'TUN';
	
	// ✅ Check if selected boutique is OUTSIDE Tunisia (show contenaire)
	const showContenaire = selectedBoutiqueId && selectedBoutique && selectedBoutique.country && selectedBoutique.country !== 'TUN';

	// ─── Fetch basket + contenaires on mount ──────────────────────────────────
	useEffect(() => {
		dispatch(getBasket({ token, id: user.data._id }));
		dispatch(getAllContenairesList({ token }));
	}, [token, dispatch, user.data._id]);

	// ─── Sorted contenaires (ascending) for nearest-fit lookup ───────────────
	const sortedContenaires = useMemo(
		() => [...(allContenaires || [])].sort((a, b) => a.volume - b.volume),
		[allContenaires],
	);

	// ─── Total volume from cart (volume per unit × quantity) ──────────────────
	const totalCartVolume = useMemo(
		() =>
			basket.reduce((acc, el) => {
				const vol = el.product?.volume || 0;
				return acc + vol * (el.quantity || 0);
			}, 0),
		[basket],
	);

	// ─── Nearest contenaire: smallest that fits, fallback = largest ───────────
	const suggestedContenaire = useMemo(() => {
		if (!sortedContenaires.length || totalCartVolume <= 0) return null;
		const fitting = sortedContenaires.find((c) => c.volume >= totalCartVolume);
		return fitting ?? sortedContenaires[sortedContenaires.length - 1];
	}, [sortedContenaires, totalCartVolume]);

	const getContenaireBadge = () => {
		if (!suggestedContenaire || totalCartVolume <= 0) return null;
		const diff = suggestedContenaire.volume - totalCartVolume;
		if (diff === 0) return { label: 'Correspondance exacte',   color: '#27ae60', bg: '#eafaf1' };
		if (diff > 0 && diff <= 5) return { label: 'Meilleure correspondance', color: '#2980b9', bg: '#eaf4fb' };
		if (diff < 0)  return { label: 'Volume dépassé',           color: '#c0392b', bg: '#fdf2f2' };
		return            { label: 'Plus proche disponible',        color: '#e67e22', bg: '#fef9ef' };
	};
	const badge = getContenaireBadge();

	// ─── Financials ───────────────────────────────────────────────────────────
	const totalHT = () => {
		let total = 0;
		basket?.forEach((el) => {
			let tmp = total + el.quantity * el?.product?.[typePrice(user.data.categories)];
			total = tmp * (1 - (el.remise ?? 0) * 0.01);
		});
		return roundToThreeDecimals(total);
	};

	const totalTCC = () => {
		const list = basket.map((el) => ({
			tva: isTunisia ? (el?.product?.Tva || 0) : 0,
			prise_product_HT: el?.product?.[typePrice(user.data.categories)],
			quantity: el?.quantity,
		}));

		const totalTvaSum = calculateTotalTva({ list }).reduce(
			(acc, { totalAmount }) => acc + totalAmount,
			0,
		);

		const totalHTAmount = totalHT();
		const timbre = isTunisia ? 1 : 0;
		return roundToThreeDecimals(Number(totalTvaSum) + Number(totalHTAmount) + parseInt(timbre));
	};

	const handleAddOrder = () => {
		const list = basket.map((item) => {
			const prise_product_HT = item?.product?.[typePrice(user.data.categories)];
			const Amount_excluding_tax = prise_product_HT * (1 + (item?.product?.Tva || 0) / 100);
			return {
				product_id: item.product._id,
				quantity: item.quantity,
				remise: item.remise ?? 0,
				prise_product_HT,
				Amount_excluding_tax,
			};
		});

		const data = {
			list: [...list],
			user_id: user.data._id,
			shop_id: selectedBoutiqueId,
			number: user.data.phone,
			date: new Date().toISOString(),
			total: totalTCC(),
			total_HT: totalHT(),
			tva: isTunisia ? 19 : 0,
			timbre: isTunisia ? 1 : 0,
		};

		setIsLoading(true);
		dispatch(addOrderByCustomer({ token, data }))
			.unwrap()
			.then(() => {
				dispatch(clearBasketCustomer({ token, id: user.data._id }));
				navigate('/client/orders/1');
			})
			.catch((err) => {
				if (err?.message === 'Ces produits ne sont pas disponibles.')
					showToast('Il y a un produit qui est désactivé', 'error');
				else showToast(err?.message || err, 'error');
			})
			.finally(() => {
				setIsLoading(false);
				setConfirmModal(false);
			});
	};

	const handleOrder = () => {
		let canOrder = true;
		const list = basket.map((el) => {
			if (el.quantity <= 0 || isNaN(el.quantity)) {
				showToast('La quantité doit être supérieure à 0.', 'warn', 'top-center', 1000);
				canOrder = false;
			}
			return { product_id: el.product._id, quantity: el.quantity };
		});

		if (!canOrder) return;

		setIsLoading(true);
		dispatch(checkStock({ token, list }))
			.unwrap()
			.then((res) => {
				if (res.data.length > 0) {
					setIsOpen(true);
					const errData = res.data.map((item) => ({
						Designation: item.designation,
						error:
							item.stock > 0
								? `Stock insuffisant pour ce produit maximum disponible : ${item.stock}`
								: 'Stock insuffisant pour ce produit',
					}));
					setErrorData(errData);
				} else {
					setConfirmModal(true);
				}
			})
			.catch((error) => {
				showToast(error.data, 'error');
			})
			.finally(() => setIsLoading(false));
	};

	const handleChangeQty = (e, item) => {
		const newQuantity = Number(e.target.value);
		if (newQuantity < 0) return;
		dispatch(updatedbasketTotalQty(basketTotalQty - item.quantity + newQuantity));
		newQuantity > item.product.capcity ? setOutOfStock(true) : setOutOfStock(false);
		dispatch(handleChangeQuantity({ productId: item.product._id, newQuantity }));
	};

	const handleDeleteCart = () => {
		dispatch(deleteBasket({ token, id: indexDeleteCart }))
			.unwrap()
			.then(() => {
				showToast('Produit supprimé du panier', 'success');
				dispatch(getBasket({ token, id: user.data._id }));
			});
		setOpenDeleteModal(false);
	};

	const totalPriceTCC = basket.reduce(
		(acc, el) =>
			acc +
			el.product?.[typePrice(user.data.categories)] *
				(isTunisia ? (1 + el.product?.Tva / 100) : 1) *
				el.quantity,
		0,
	);

	const handleCalculatePrice = (item) =>
		roundToThreeDecimals(
			item.product?.[typePrice(user.data.categories)] *
				(isTunisia ? (1 + item.product?.Tva / 100) : 1) *
				item.quantity,
		);

	const handleChangebasket = async () => {
		const data = basket.map((product) => {
			const payload = {
				quantity: Number(product.quantity),
				total: roundToThreeDecimals(
					product.quantity * product.product?.[typePrice(user.data.categories)],
				),
			};
			return dispatch(udpateStockProduitBasket({ token, id: product._id, data: payload }));
		});

		await Promise.all(data).then(() => {
			showToast('Produits mis à jour avec succès', 'success', 'top-center', 1000);
			setOldBasketTotalQty(basketTotalQty);
		});
	};

	return (
		<div id='pr-cart-client'>
			{isOpen && (
				<DataErrorModal
					isOpen={isOpen}
					closeModal={() => setIsOpen(false)}
					errorData={errorData}
					width='35rem'
					height='auto'
				/>
			)}

			<header className='cart-header'>
				<div className='cart-header__brand'>
					<span className='cart-header__icon'>🛒</span>
					<h1 className='cart-header__title'>Mon Panier</h1>
					<span className='cart-header__badge'>{basketTotalQty} articles</span>
				</div>
				<div className='cart-header__controls'>
					<Link to='/client/products/1' className='continue-shopping'>
						<icons.IoIosArrowBack />
						<span>Continuer mes achats</span>
					</Link>
				</div>
			</header>

			<Fragment>
				<ConfirmOrderModal
					setConfirmModal={setConfirmModal}
					confirmModal={confirmModal}
					handleAddOrder={handleAddOrder}
					loading={isLoading}
				/>
			</Fragment>

			<div className='cart-content'>
				{basket?.length === 0 ? (
					<div className='empty-cart'>
						<span className='empty-cart__icon'>🛍️</span>
						<p>Votre panier est vide</p>
						<Link to='/client/products/1' className='empty-cart__btn'>
							Commencer mes achats
						</Link>
					</div>
				) : (
					<>
						<div className='cart-actions'>
							<button
								disabled={basketTotalQty === oldBaksetTotalQty}
								className={`update-btn ${basketTotalQty !== oldBaksetTotalQty ? 'active' : ''}`}
								onClick={handleChangebasket}
							>
								<icons.FiRefreshCw />
								<span>Actualiser mon panier</span>
							</button>
						</div>

						<div className='cart-layout'>
							{/* ── Product list ── */}
							<div className='cart-products'>
								{basket?.map((item) => (
									<article className='cart-item' key={item.product?._id}>
										<div className='cart-item__image'>
											<img
												src={item.product?.image?.secure_url}
												alt={item.product?.designation}
											/>
										</div>
										<div className='cart-item__details'>
											<h4 className='cart-item__title'>{item.product?.designation}</h4>
											<p className='cart-item__company'>
												<icons.FiBriefcase />
												<span>{item.product?.created_By?.company?.name}</span>
											</p>
											<span className='cart-item__price'>
												{roundToThreeDecimals(
													item.product?.[typePrice(user.data.categories)] *
														(isTunisia ? (1 + item.product?.Tva / 100) : 1),
												)}{' '}
												TND / unité
											</span>
											{/* Volume per line */}
											{item.product?.volume != null && (
												<span style={{ fontSize: '0.78rem', color: '#888', marginTop: '2px', display: 'block' }}>
													Volume : {roundToThreeDecimals(item.product.volume * item.quantity)} m³
												</span>
											)}
										</div>
										<div className='cart-item__actions'>
											<div className='quantity-control'>
												<input
													type='number'
													min='1'
													value={parseInt(item.quantity, 10)}
													onChange={(e) => handleChangeQty(e, item)}
												/>
											</div>
											<div className='item-total'>
												<span>{handleCalculatePrice(item)} TND</span>
											</div>
											<button
												className='delete-item'
												onClick={() => {
													setOpenDeleteModal(true);
													setIndexDeleteCart(item._id);
												}}
												title='Supprimer'
											>
												<icons.FaTrashAlt />
											</button>
										</div>
									</article>
								))}
							</div>

							{/* ── Summary sidebar ── */}
							<div className='cart-summary'>
								<h3 className='summary-title'>Récapitulatif</h3>

								<div className='shop-selector'>
									<label>Choisissez une boutique</label>
									<select
										onChange={(e) => setSelectedBoutiqueId(e.target.value)}
										value={selectedBoutiqueId}
										className='shop-select'
									>
										<option value=''>Sélectionner une boutique</option>
										{BoutiquesData.map((boutique, index) => (
											<option key={index} value={boutique.id}>
												{boutique.name} - {boutique.ville}
											</option>
										))}
									</select>
								</div>

								{selectedBoutiqueId && !isTunisia && (
									<div className='non-tunisia-notice'>
										<span>Commande hors Tunisie - TVA et timbre non applicables</span>
									</div>
								)}

								<div className='summary-details'>
									<div className='summary-row'>
										<span>Total articles</span>
										<strong>
											{basketTotalQty}{' '}
											{basketTotalQty === 1 ? 'article' : 'articles'}
										</strong>
									</div>
									{/* Total volume row */}
									{totalCartVolume > 0 && (
										<div className='summary-row'>
											<span>Volume total</span>
											<strong>{roundToThreeDecimals(totalCartVolume)} m³</strong>
										</div>
									)}
									<div className='summary-row total'>
										<span>Total TTC</span>
										<strong className='total-price'>
											{roundToThreeDecimals(totalPriceTCC)} TND
										</strong>
									</div>
								</div>

								{/* ── Contenaire suggestion - ONLY SHOW when shop is OUTSIDE Tunisia ── */}
								{showContenaire && suggestedContenaire && totalCartVolume > 0 && (
									<div
										style={{
											marginTop: '14px',
											marginBottom: '13px',
											padding: '12px 14px',
											borderRadius: '8px',
											border: `1.5px solid ${badge?.color ?? '#ccc'}`,
											backgroundColor: badge?.bg ?? '#fafafa',
										}}
									>
										<div
											style={{
												fontSize: '0.7rem',
												fontWeight: 700,
												color: badge?.color ?? '#555',
												textTransform: 'uppercase',
												letterSpacing: '0.05em',
												marginBottom: '6px',
											}}
										>
											📦 Contenaire suggéré
										</div>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
											}}
										>
											<span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>
												{suggestedContenaire.nom}
											</span>
											<span style={{ fontWeight: 600, fontSize: '0.88rem', color: badge?.color ?? '#333' }}>
												{roundToThreeDecimals(suggestedContenaire.volume)} m³
											</span>
										</div>
										<div style={{ fontSize: '0.75rem', color: '#777', marginTop: '4px' }}>
											{badge?.label} — votre commande :{' '}
											{roundToThreeDecimals(totalCartVolume)} m³
										</div>
									</div>
								)}

								<ButtonComponent
									disabled={!basket.length || outOfStock || !selectedBoutiqueId}
									loading={isLoading}
									className='checkout-btn'
									onClick={(e) => {
										e.preventDefault();
										if (!selectedBoutiqueId) {
											showToast('Veuillez choisir une boutique', 'warn');
											return;
										}
										handleOrder();
									}}
									name='Valider la commande'
								/>
							</div>
						</div>
					</>
				)}
			</div>

			{openDeleteModal && (
				<DeleteConfirmationModal
					onDelete={handleDeleteCart}
					onCancel={() => setOpenDeleteModal(false)}
				/>
			)}
		</div>
	);
};

export default ProductCartClient;